/**
 * Textos y deep-link de una mención, en un solo lugar (#277).
 *
 * Los usan la bandeja (`/notifications`) y la notificación del navegador. **Tienen que decir lo
 * mismo:** si el toast dice una cosa y la fila de la bandeja otra, son dos verdades del mismo
 * evento y el Operador no sabe cuál creer. Por eso viven acá y no duplicados en cada pantalla.
 *
 * La anatomía es la del doc de usuario (`features/Admin/Conversaciones/Notificaciones`):
 *   Línea 1 — `[Nombre] te mencionó en una nota`
 *   Línea 2 — `en el presupuesto #XXXXX` · `en la conversación con … · Canal: …`
 *   Línea 3 — el preview de la nota
 */
import {
  AppNotification,
  NotificationModule,
} from "@/services/conversations.api";

export const MODULE_LABEL: Record<NotificationModule, string> = {
  contact: "Contacto",
  quote: "Presupuesto",
  order: "Pedido",
  conversation: "Conversación",
};

/** L2: preposición por módulo. El nombre/nº del registro lo agrega `entityLabel`. */
export function contextPrefix(module: NotificationModule): string {
  switch (module) {
    case "conversation":
      return "en la conversación";
    case "contact":
      return "en el historial de";
    case "quote":
      return "en el presupuesto";
    case "order":
      return "en el pedido";
    default:
      return "";
  }
}

/** El nombre del registro cuando el backend lo resolvió; si no, el label del módulo. */
export function entityLabel(n: AppNotification): string {
  return n.entityName ?? MODULE_LABEL[n.module];
}

/** ` · Canal: WhatsApp Ventas`, sólo en conversaciones y sólo si el backend lo mandó. */
export function channelSuffix(n: AppNotification): string {
  return n.module === "conversation" && n.channelName
    ? ` · Canal: ${n.channelName}`
    : "";
}

/** L1 — el título del toast. */
export function mentionTitle(n: AppNotification): string {
  return `${n.actorName} te mencionó en una nota`;
}

/** L2 completa, en texto plano: lo que la bandeja renderiza con un link adentro. */
export function mentionContext(n: AppNotification): string {
  return `${contextPrefix(n.module)} ${entityLabel(n)}${channelSuffix(n)}`;
}

/**
 * Destino (deep-link) por módulo.
 *
 * Las menciones nacen de una nota, así que además del registro va el `noteId` en `?note=`: el
 * historial de la ficha lo usa para hacer foco en esa nota concreta en vez de dejar al Operador
 * buscándola a mano en un timeline de 60 entradas.
 */
export function recordHref(n: AppNotification): string | null {
  const note = n.noteId ? `?note=${encodeURIComponent(n.noteId)}` : "";
  switch (n.module) {
    case "conversation":
      return n.conversationId
        ? `/conversations?open=${n.conversationId}`
        : null;
    case "contact":
      return n.entityId ? `/contacts/${n.entityId}${note}` : null;
    case "quote":
      return n.entityId ? `/quotes/${n.entityId}${note}` : null;
    case "order":
      return n.entityId ? `/orders/${n.entityId}${note}` : null;
    default:
      return null;
  }
}
